// The landing page's Master's abroad section (#4967, epic #4962) and the
// /masters page (#4985), every word in one place. The section shows only to
// India visitors (utils/visitorRegion.js); /masters is for every region
// (founder 2026-10-21); both only while india_masters is on for the visitor.
//
// What the copy says (founder, 2026-10-20): how Stratia WILL help a student
// apply for a master's, not what we are doing behind the scenes. Every point
// is in the future tense ("will"), because none of it is live yet: the
// section is a waitlist, and each point is a build step of epic #4962.
//
// The server is the authority on PROGRAMS and CONSENT_VERSION
// (cloud_functions/profile_manager_v2/masters_waitlist.py); test_masters_waitlist.py
// pins both equal to this file. Keep this module import-free.

export const PROGRAMS = [
    { value: 'computer_science', label: 'Computer Science' },
    { value: 'data_science', label: 'Data Science' },
    { value: 'electrical_computer_engineering', label: 'Electrical & Computer Engineering' },
    { value: 'business_analytics', label: 'Business Analytics' },
    { value: 'information_systems', label: 'Information Systems' },
    { value: 'mechanical_engineering', label: 'Mechanical Engineering' },
    { value: 'civil_environmental_engineering', label: 'Civil & Environmental Engineering' },
    { value: 'industrial_engineering', label: 'Industrial Engineering & Engineering Management' },
    { value: 'chemical_engineering', label: 'Chemical Engineering' },
    { value: 'biomedical_engineering', label: 'Biomedical Engineering' },
    { value: 'biotechnology', label: 'Biotechnology & Biological Sciences' },
    { value: 'public_health', label: 'Public Health' },
    { value: 'finance', label: 'Finance' },
    { value: 'mba', label: 'MBA' },
];

// A change of the consent words is a new version, here AND on the server, so
// every stored sign-up records which words it agreed to.
export const CONSENT_VERSION = 'masters-waitlist-2026-10-20';

export const MASTERS_COPY = {
    eyebrow: "Master's in the US",
    title: "Applying for a master's in the US?",
    // The platform today, true now (founder: "talk about the power of its
    // platform... and its reach"); its figures come from the landing stats
    // the proof strip serves (live, thresholded), never typed here.
    platform: "Stratia already plans US college applications from each school's own data, with the source on every answer.",
    reachLabels: {
        college_pages: 'colleges, each with its own page',
        video_views: 'views on our videos',
        countries: 'countries our visitors come from',
    },
    body: "Next, it will help you apply for a master's, one program at a time:",
    points: [
        "See what each program asks for: the GRE, English test scores, and whether it accepts a three-year bachelor's degree.",
        "Check where you stand against each program's own requirements before you spend an application fee.",
        'Keep every deadline for the Fall and Spring intakes on one list.',
        "Write your statement of purpose to each program's own prompts.",
        'Compare tuition, assistantships and funding, and which programs qualify for the STEM work extension.',
    ],
    trust: "Every fact will come from the program's own page, and no university pays us. "
        + "Join the waitlist and we'll email you when it opens.",
    programsLabel: 'Which programs interest you?',
    programsHelp: 'Pick one or more.',
    emailLabel: 'Your email',
    signedInAs: (email) => `We'll use ${email}, the address you signed in with.`,
    consent: "Email me when Stratia's master's program pages open. Every email has a link to leave the list.",
    submit: 'Join the waitlist',
    submitting: 'Joining...',
    joined: (email) => `You're on the list. We'll email ${email} when program pages open.`,
    rejoined: "You were already on the list. We've added the programs you picked.",
    errors: {
        email: 'Enter an email address, like name@example.com.',
        programs: 'Pick at least one program.',
        consent: 'Tick the box so we can email you when program pages open.',
        rate_limited: 'Too many sign-ups from this connection. Please try again in an hour.',
        not_found: 'This waitlist is closed right now.',
        broke: 'Something went wrong on our side - please try again in a moment.',
    },
};

// The /masters page (#4985, founder pick A of the mock round: the waitlist
// first, the reasons below). For every region; only the price and the
// international points change, and both come from the region strategy
// (config/regions.js `mastersPrice()`, `international`), never a country
// check here. Future tense throughout: nothing master's-specific is live.
export const MASTERS_PAGE_COPY = {
    metaTitle: "Applying for a master's in the US | Stratia",
    metaDescription: "How Stratia will help you apply for a master's in the US, one program at a time. "
        + 'Join the waitlist for program pages.',
    eyebrow: "Master's in the US",
    title: "Applying for a master's in the US?",
    lede: "Stratia will help you apply, one program at a time. Join now and we'll email you when program pages open.",
    // The planned price, in the visitor's currency, as the price when
    // program pages open (nothing is sold today). [before, figure, after].
    price: (label) => ['Planned price: ', label,
        ' for your application season, once program pages open. Joining the waitlist is free.'],
    // The section's lines, shared word for word (design spec section 5).
    platform: MASTERS_COPY.platform,
    pointsLead: MASTERS_COPY.body,
    // For every applicant, wherever they apply from.
    points: [
        'See what each program asks for, from the GRE to letters of recommendation.',
        "Check where you stand against each program's own requirements before you spend an application fee.",
        'Keep every deadline for the Fall and Spring intakes on one list.',
        "Write your statement of purpose to each program's own prompts.",
        'Compare tuition, assistantships and funding across the programs you are considering.',
    ],
    // Added when the visitor's region is international (India today).
    internationalHeading: 'For international applicants',
    internationalPoints: [
        'See which English tests each program accepts, and the scores it asks for.',
        "Find out whether a program accepts a three-year bachelor's degree.",
        'See which programs qualify for the STEM work extension after you graduate.',
    ],
    trust: MASTERS_COPY.trust,
};

// The four programs /masters shows first (mock A draws these four, two by
// two, with the rest behind one button; the design spec names the button
// "Show 10 more programs"), so the email and the button stay on a phone's
// first screen. What is posted is still in PROGRAMS order.
export const FEATURED_PROGRAMS = ['computer_science', 'data_science', 'biotechnology', 'public_health'];
export const moreFieldsLabel = (n) => `Show ${n} more programs`;

// The default region's home-page line to /masters (#4985), low on the page.
export const MASTERS_LINK_COPY = {
    lead: "Applying for a master's instead?",
    link: 'See how Stratia will help',
    to: '/masters',
};
